import { Plus, Search, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { driverApi } from '../Driver/driverApi';
import { getAllRoles } from '../Onboarding/onboardingApi';
import { getAllUsers, getUsersByRole } from '../OrgManagement/employeeApiService';
import { ProfileImage } from '../ui';
import { patrollingApi } from './PatrollingApi';

const SelectPatrolingEmployees = ({
  isOpen,
  onClose,
  onConfirm,
  dialogTitle = "Assign Employees",
  availableEntities = [],
  initialSelectedIds = [],
  employeeGroupAssignments = {}, // Object<employeeId, groupNumber>
  patrolData = null // Contains start_date, start_time, end_date, end_time for conflict checking
}) => {
  const [selectedDesignations, setSelectedDesignations] = useState([]); // Array of { designation, employees, searchTerm, filteredEmployees, isLoading }
  const [designations, setDesignations] = useState([]);
  const [showDesignationDropdown, setShowDesignationDropdown] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState(new Set(initialSelectedIds.map(id => id?.toString())));
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [employeeConflicts, setEmployeeConflicts] = useState(new Map()); // Map<employeeId, boolean> - true if employee has conflict
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
  const listDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideList = listDropdownRef.current && !listDropdownRef.current.contains(event.target);
      
      if (isOutsideList) {
        setShowDesignationDropdown(false);
      }
    };

    if (showDesignationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDesignationDropdown]);

  // Convert date and time to ISO8601 timestamp in IST
  const convertToISO8601 = (dateStr, timeStr) => {
    try {
      // dateStr is in format "DD-MM-YYYY"
      // timeStr is in format "HH:MM AM/PM"
      // We need to treat this as IST (UTC+5:30) and convert to ISO8601
      
      // Parse date
      const [day, month, year] = dateStr.split('-').map(Number);
      
      // Parse time
      const timeMatch = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (!timeMatch) {
        throw new Error('Invalid time format');
      }
      
      let hours = parseInt(timeMatch[1]);
      const minutes = parseInt(timeMatch[2]);
      const period = timeMatch[3].toUpperCase();
      
      // Convert to 24-hour format
      if (period === 'PM' && hours !== 12) {
        hours += 12;
      } else if (period === 'AM' && hours === 12) {
        hours = 0;
      }
      
      // Create date string in IST format (UTC+5:30)
      // Format: YYYY-MM-DDTHH:mm:ss+05:30
      const yearStr = String(year).padStart(4, '0');
      const monthStr = String(month).padStart(2, '0');
      const dayStr = String(day).padStart(2, '0');
      const hoursStr = String(hours).padStart(2, '0');
      const minutesStr = String(minutes).padStart(2, '0');
      
      // Return ISO8601 format with IST timezone (+05:30)
      return `${yearStr}-${monthStr}-${dayStr}T${hoursStr}:${minutesStr}:00+05:30`;
    } catch (error) {
      console.error('Error converting date/time to ISO8601:', error);
      throw new Error(`Invalid date/time format: ${dateStr} ${timeStr}`);
    }
  };

  // Check if two time ranges overlap
  const hasTimeOverlap = (start1, end1, start2, end2) => {
    const start1Time = new Date(start1).getTime();
    const end1Time = new Date(end1).getTime();
    const start2Time = new Date(start2).getTime();
    const end2Time = new Date(end2).getTime();
    
    // Check if ranges overlap: start1 < end2 && start2 < end1
    return start1Time < end2Time && start2Time < end1Time;
  };

  // Check for conflicts with existing patrols
  const checkEmployeeConflicts = useCallback(async () => {
    if (!patrolData || !patrolData.start_date || !patrolData.start_time || !patrolData.end_date || !patrolData.end_time) {
      console.log('No patrol data provided for conflict checking');
      setEmployeeConflicts(new Map());
      return;
    }

    setIsCheckingConflicts(true);
    try {
      // Convert patrol data times to ISO8601
      const newPatrolStart = convertToISO8601(patrolData.start_date, patrolData.start_time);
      const newPatrolEnd = convertToISO8601(patrolData.end_date, patrolData.end_time);

      // Fetch all existing patrols
      console.log('Checking for employee conflicts...');
      const allPatrolsResponse = await patrollingApi.getAll({ limit: 100 });
      const allPatrols = allPatrolsResponse.patrols || [];

      // Create a map of employee conflicts
      const conflictsMap = new Map();

      // Check each existing patrol for time overlap and employee conflicts
      allPatrols.forEach(patrol => {
        if (!patrol.patrol_start_timestamp || !patrol.patrol_end_timestamp) {
          return;
        }

        // Check if time ranges overlap
        if (hasTimeOverlap(
          newPatrolStart,
          newPatrolEnd,
          patrol.patrol_start_timestamp,
          patrol.patrol_end_timestamp
        )) {
          // If times overlap, check if any employees in this patrol conflict
          if (patrol.patrol_details && Array.isArray(patrol.patrol_details)) {
            patrol.patrol_details.forEach(detail => {
              if (detail.user_ids && Array.isArray(detail.user_ids)) {
                detail.user_ids.forEach(userId => {
                  const employeeId = userId?.toString();
                  if (employeeId) {
                    conflictsMap.set(employeeId, true);
                  }
                });
              }
            });
          }
        }
      });

      console.log(`Found ${conflictsMap.size} employees with conflicts`);
      setEmployeeConflicts(conflictsMap);
    } catch (error) {
      console.error('Error checking employee conflicts:', error);
      setEmployeeConflicts(new Map());
    } finally {
      setIsCheckingConflicts(false);
    }
  }, [patrolData]);

  // Fetch designations/roles on mount and check conflicts
  useEffect(() => {
    if (isOpen) {
      fetchDesignations();
      setSelectedDesignations([]);
      setSelectedEmployeeIds(new Set(initialSelectedIds.map(id => id?.toString())));
      setShowDesignationDropdown(false);
      setEmployeeConflicts(new Map());
      
      // Check for conflicts when dialog opens
      if (patrolData) {
        checkEmployeeConflicts();
      }
    }
  }, [isOpen, initialSelectedIds, patrolData, checkEmployeeConflicts]);

  // Helper function to filter employees
  const filterEmployees = (employees, searchTerm) => {
    if (!searchTerm) {
      return employees;
    }

    const query = searchTerm.toLowerCase();
    return employees.filter(employee => {
      const id = (employee.user_id || employee.id || employee.employee_id || '').toLowerCase();
      const name = (employee.user_name || employee.name || '').toLowerCase();
      const designation = (employee.spec_role || employee.role || '').toLowerCase();
      const site = (employee.site_name || employee.site || '').toLowerCase();
      return id.includes(query) || name.includes(query) || designation.includes(query) || site.includes(query);
    });
  };

  const fetchDesignations = async () => {
    setIsLoadingRoles(true);
    try {
      const rolesData = await getAllRoles();
      if (Array.isArray(rolesData)) {
        setDesignations(rolesData);
      } else if (rolesData?.roles && Array.isArray(rolesData.roles)) {
        setDesignations(rolesData.roles);
      } else {
        console.warn('Unexpected roles format:', rolesData);
        setDesignations([]);
      }
    } catch (error) {
      console.error('Error fetching designations:', error);
      setDesignations([]);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const fetchEmployeesByRole = async (role, designationId) => {
    try {
      let employeesData = [];
      let allEmployees = [];
      
      if (role && role !== 'all') {
        // Extract role information
        const roleObj = typeof role === 'string' ? { role_name: role } : role;
        const specRole = roleObj.spec_role;
        const roleName = roleObj.role_name;
        const displayName = roleObj.display_name;
        
        console.log('🔍 Fetching employees for role:', { specRole, roleName, displayName, fullRole: role });
        
        // Check if the role is "driver" (case-insensitive)
        if (specRole?.toLowerCase() === 'driver' || roleName?.toLowerCase() === 'driver') {
          console.log('Fetching drivers using driverApi.fetchAllDrivers...');
          employeesData = await driverApi.fetchAllDrivers();
        } else if (specRole) {
          // If spec_role exists, use it to fetch from API
          console.log('Fetching by spec_role:', specRole);
          employeesData = await getUsersByRole(specRole);
        } else {
          // If no spec_role, fetch all employees and filter by role_name
          console.log('Fetching all employees to filter by role_name:', roleName);
          employeesData = await getAllUsers();
        }
        
        // Handle different response formats
        if (Array.isArray(employeesData)) {
          allEmployees = employeesData;
        } else if (employeesData?.users && Array.isArray(employeesData.users)) {
          allEmployees = employeesData.users;
        } else if (employeesData?.data && Array.isArray(employeesData.data)) {
          allEmployees = employeesData.data;
        } else {
          console.warn('Unexpected employees format:', employeesData);
          allEmployees = [];
        }

        // Filter by role_name if no spec_role was used
        if (!specRole && roleName) {
          console.log('Filtering employees by role_name:', roleName);
          allEmployees = allEmployees.filter(emp => {
            const empRoleName = emp.role_name || emp.role || emp.spec_role;
            return empRoleName?.toLowerCase() === roleName.toLowerCase();
          });
        }
      } else {
        // Fetch all employees
        employeesData = await getAllUsers();
        
        // Handle different response formats
        if (Array.isArray(employeesData)) {
          allEmployees = employeesData;
        } else if (employeesData?.users && Array.isArray(employeesData.users)) {
          allEmployees = employeesData.users;
        } else if (employeesData?.data && Array.isArray(employeesData.data)) {
          allEmployees = employeesData.data;
        } else {
          allEmployees = [];
        }
      }

      console.log('✅ Fetched employees count:', allEmployees.length, 'Sample employee:', allEmployees[0]);

      // Update the designation with employees and filter based on current search term
      setSelectedDesignations(prev => prev.map(item => {
        if (item.id === designationId) {
          const filtered = filterEmployees(allEmployees, item.searchTerm);
          console.log('📊 Filtered employees count:', filtered.length);
          return { ...item, employees: allEmployees, filteredEmployees: filtered, isLoading: false };
        }
        return item;
      }));
    } catch (error) {
      console.error('Error fetching employees:', error);
      setSelectedDesignations(prev => prev.map(item => 
        item.id === designationId 
          ? { ...item, employees: [], filteredEmployees: [], isLoading: false }
          : item
      ));
    }
  };

  const handleDesignationSelect = (designation) => {
    // Check if designation is already selected
    const designationKey = getDesignationKey(designation);
    const isAlreadySelected = selectedDesignations.some(d => getDesignationKey(d.designation) === designationKey);
    
    if (isAlreadySelected) {
      setShowDesignationDropdown(false);
      return;
    }

    // Create new designation entry
    const designationId = Date.now().toString();
    const newDesignation = {
      id: designationId,
      designation,
      employees: [],
      filteredEmployees: [],
      searchTerm: '',
      isLoading: true
    };

    setSelectedDesignations(prev => [...prev, newDesignation]);
    setShowDesignationDropdown(false);
    
    // Fetch employees for this designation
    fetchEmployeesByRole(designation, designationId);
  };

  const handleClearDesignation = (designationId) => {
    // Remove employees from selectedEmployeeIds when removing designation
    const designationToRemove = selectedDesignations.find(d => d.id === designationId);
    if (designationToRemove) {
      const employeeIdsToRemove = designationToRemove.employees.map(emp => getEmployeeId(emp));
      setSelectedEmployeeIds(prev => {
        const newSet = new Set(prev);
        employeeIdsToRemove.forEach(id => newSet.delete(id));
        return newSet;
      });
    }

    setSelectedDesignations(prev => prev.filter(d => d.id !== designationId));
  };

  const handleSearchTermChange = (designationId, searchTerm) => {
    setSelectedDesignations(prev => prev.map(item => {
      if (item.id === designationId) {
        const filtered = filterEmployees(item.employees, searchTerm);
        return { ...item, searchTerm, filteredEmployees: filtered };
      }
      return item;
    }));
  };

  const getDesignationKey = (designation) => {
    if (!designation) return '';
    if (designation === 'all') return 'all';
    if (typeof designation === 'string') return designation;
    return designation.role_name || designation.display_name || designation.name || '';
  };

  const getEmployeeId = (employee) => {
    return (employee.user_id || employee.id || employee.employee_id || '').toString();
  };

  const getEmployeeName = (employee) => {
    return employee.user_name || employee.name || 'Unknown';
  };

  const getEmployeeDesignation = (employee) => {
    return employee.spec_role || employee.role || employee.designation || 'Employee';
  };

  const getEmployeeIdDisplay = (employee) => {
    return employee.employee_id || employee.user_id || employee.id || 'N/A';
  };

  const handleToggleSelection = (employeeId) => {
    // Prevent selection if employee has a conflict with another shift
    if (employeeConflicts.has(employeeId)) {
      alert(`This employee is assigned to another shift during the selected time period. Please select a different employee.`);
      return;
    }
    
    // Prevent selection if employee is already assigned to another group
    if (employeeGroupAssignments[employeeId] !== undefined) {
      const assignedGroup = employeeGroupAssignments[employeeId];
      alert(`This employee has already been selected in group ${assignedGroup}. Please select a different employee.`);
      return;
    }
    
    setSelectedEmployeeIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(employeeId)) {
        newSet.delete(employeeId);
      } else {
        newSet.add(employeeId);
      }
      return newSet;
    });
  };

  const handleConfirm = () => {
    // Collect full employee objects (with id and name) for selected employees
    // Use a Map to deduplicate employees by ID (in case same employee appears in multiple designations)
    const selectedEmployeesMap = new Map();
    
    selectedDesignations.forEach(designationItem => {
      designationItem.employees.forEach(employee => {
        const employeeId = getEmployeeId(employee);
        if (selectedEmployeeIds.has(employeeId)) {
          // Only add if not already in the map (prevents duplicates from multiple designations)
          if (!selectedEmployeesMap.has(employeeId)) {
            selectedEmployeesMap.set(employeeId, {
              id: employeeId,
              name: getEmployeeName(employee),
              designation: getEmployeeDesignation(employee),
            });
          }
        }
      });
    });
    
    // Convert map to array (removes duplicates automatically)
    const selectedEmployees = Array.from(selectedEmployeesMap.values());
    
    console.log('Selected employees (deduplicated):', selectedEmployees.length, 'out of', selectedEmployeeIds.size, 'selected IDs');
    
    // Return employee objects with id and name
    onConfirm(selectedEmployees);
  };

  const getDesignationDisplayName = (designation) => {
    if (!designation) return 'Select Designation';
    if (designation === 'all') return 'All Employees';
    if (typeof designation === 'string') return designation;
    return designation.display_name || designation.role_name || designation.name || 'Select Designation';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-200 rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 bg-white rounded-t-lg">
          <h3 className="text-xl font-bold text-gray-900">{dialogTitle}</h3>
          <button
            onClick={onClose}
            className="p-2 bg-gray-300 hover:bg-gray-400 rounded transition-colors"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto flex flex-col bg-white m-1 rounded-lg p-1 space-y-4">
          {selectedDesignations.length === 0 ? (
            /* Empty State - No Designation Selected */
            <div className="flex items-center justify-center h-full min-h-[320px]">
              <p className="text-gray-500 text-lg">
                Select Designation to assign employees for patrolling
              </p>
            </div>
          ) : (
            /* Designation Widgets */
            <>
              {selectedDesignations.map((designationItem) => (
                <div
                  key={designationItem.id}
                  className="border-2 border-dashed border-gray-300 rounded-lg bg-white"
                >
                  {/* Selected Designation Header */}
                  <div className="flex justify-between items-center p-4 border-b border-gray-200">
                    <span className="text-gray-900 font-medium">
                      {getDesignationDisplayName(designationItem.designation)}
                    </span>
                    <button
                      onClick={() => handleClearDesignation(designationItem.id)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      title="Close designation"
                    >
                      <X className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>

                  {/* Search Bar */}
                  <div className="p-4 border-b border-gray-200">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={designationItem.searchTerm}
                        onChange={(e) => handleSearchTermChange(designationItem.id, e.target.value)}
                        placeholder="Search by site"
                        className="w-full pl-10 pr-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </div>

                  {/* Employee List */}
                  <div className="p-4 max-h-[400px] overflow-y-auto">
                    {isCheckingConflicts && designationItem.filteredEmployees.length > 0 && (
                      <div className="text-center text-blue-600 text-sm py-2 mb-2">
                        Checking for shift conflicts...
                      </div>
                    )}
                    {designationItem.isLoading ? (
                      <div className="text-center text-gray-500 py-8">
                        Loading employees...
                      </div>
                    ) : designationItem.filteredEmployees.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">
                        No employees found
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {designationItem.filteredEmployees.map((employee) => {
                          const employeeId = getEmployeeId(employee);
                          const isSelected = selectedEmployeeIds.has(employeeId);
                          const assignedGroup = employeeGroupAssignments[employeeId];
                          const isAssignedToOtherGroup = assignedGroup !== undefined;
                          const hasConflict = employeeConflicts.has(employeeId);
                          const isDisabled = isAssignedToOtherGroup || hasConflict;

                          return (
                            <div
                              key={employeeId}
                              onClick={() => !isDisabled && handleToggleSelection(employeeId)}
                              className={`bg-white/70 backdrop-blur-md border rounded-lg p-4 flex items-center gap-4 transition-colors shadow-sm ${
                                isDisabled
                                  ? 'border-red-300 bg-red-50/50 cursor-not-allowed opacity-75'
                                  : 'border-gray-200 hover:bg-white/90 cursor-pointer'
                              }`}
                            >
                              {/* Profile Avatar */}
                              <ProfileImage
                                userId={employee.user_id || employee.id || employee.employee_id}
                                userName={getEmployeeName(employee)}
                                className="w-12 h-12"
                                showLoading={false}
                              />

                              {/* Employee Info */}
                              <div className="flex-1">
                                <p className="font-bold text-gray-900 text-base">
                                  {getEmployeeName(employee)}
                                </p>
                                <p className="text-sm text-gray-600 mt-1">
                                  {getEmployeeDesignation(employee)} • ID: {getEmployeeIdDisplay(employee)}
                                </p>
                                {hasConflict ? (
                                  <p className="text-sm text-red-600 font-medium mt-1">
                                    Assigned to other shift
                                  </p>
                                ) : isAssignedToOtherGroup ? (
                                  <p className="text-sm text-orange-600 font-medium mt-1">
                                    Already selected in group {assignedGroup}
                                  </p>
                                ) : (
                                  <p className="text-sm text-green-600 font-medium mt-1">
                                    Available
                                  </p>
                                )}
                              </div>

                              {/* Checkbox */}
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={() => {}} // Empty handler since parent div handles the click
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent double toggle
                                  if (isDisabled) {
                                    e.preventDefault(); // Prevent selection if disabled
                                  }
                                }}
                                className={`w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500 ${
                                  isDisabled
                                    ? 'cursor-not-allowed opacity-50'
                                    : 'cursor-pointer pointer-events-none'
                                }`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Select Designation Button (below the widgets) */}
        <div className="p-4 relative" ref={listDropdownRef}>
          <button
            onClick={() => setShowDesignationDropdown(!showDesignationDropdown)}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 border-2 border-dashed border-gray-400 rounded-lg bg-white hover:bg-gray-50 transition-colors"
          >
            <Plus className="w-6 h-6 text-gray-900" />
            <span className="text-gray-900 font-medium">Select Designation</span>
          </button>
          
          {/* Designation Dropdown */}
          {showDesignationDropdown && (
            <div className="absolute bottom-full left-4 right-4 mb-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto z-20">
              <div className="p-2">
                {isLoadingRoles ? (
                  <div className="p-4 text-center text-gray-500">Loading designations...</div>
                ) : designations.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">No designations available</div>
                ) : (
                  <>
                    <button
                      onClick={() => handleDesignationSelect('all')}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded transition-colors"
                    >
                      <span className="text-gray-900">All Employees</span>
                    </button>
                    {designations.map((designation, index) => {
                      const designationKey = getDesignationKey(designation);
                      const isSelected = selectedDesignations.some(d => getDesignationKey(d.designation) === designationKey);
                      const displayName = typeof designation === 'string'
                        ? designation
                        : (designation.display_name || designation.role_name || designation.name || 'Unknown');
                      
                      return (
                        <button
                          key={index}
                          onClick={() => handleDesignationSelect(designation)}
                          disabled={isSelected}
                          className={`w-full text-left px-4 py-2 hover:bg-gray-100 rounded transition-colors ${
                            isSelected ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <span className="text-gray-900">{displayName}</span>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Confirm Button */}
        <div className="p-4 bg-white rounded-b-lg">
          <button
            onClick={handleConfirm}
            disabled={selectedEmployeeIds.size === 0}
            className={`
              w-full py-3 rounded-lg font-medium transition-colors
              ${selectedEmployeeIds.size > 0
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }
            `}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectPatrolingEmployees;
